<script setup>
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';

// `ScheduledMessagesList.vue` (reused above this form) only renders the list of
// already-pending messages — it has no create UI of its own (the only existing
// create UI, `ScheduleMessageModal.vue`, is composer-embedded and drafts the
// message content from the reply box). This form fills that gap for the lightbox.
const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const isAdding = ref(false);
const isSubmitting = ref(false);
const form = reactive({ content: '', scheduledAt: '' });

const resetForm = () => {
  form.content = '';
  form.scheduledAt = '';
  isAdding.value = false;
};

const createScheduledMessage = async () => {
  if (!form.content.trim() || !form.scheduledAt) return;
  isSubmitting.value = true;
  try {
    await store.dispatch('scheduledMessages/create', {
      conversationId: props.conversationId,
      content: form.content,
      scheduledAt: form.scheduledAt,
    });
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.SUCCESS_MESSAGE'));
    resetForm();
  } catch (error) {
    useAlert(t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div class="mt-2">
    <div
      v-if="isAdding"
      class="flex flex-col gap-1.5 px-2.5 py-2 rounded-lg bg-n-solid-1 border border-n-weak"
    >
      <textarea
        v-model="form.content"
        rows="2"
        class="reply-composer--input"
        :placeholder="
          t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.COMPOSE_PLACEHOLDER')
        "
      />
      <input v-model="form.scheduledAt" type="datetime-local" class="reply-composer--input" />
      <div class="flex items-center justify-end gap-2">
        <Button
          size="xs"
          variant="ghost"
          color="slate"
          :label="t('DIALOG.BUTTONS.CANCEL')"
          :disabled="isSubmitting"
          @click="resetForm"
        />
        <Button
          size="xs"
          :label="t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.SCHEDULE')"
          :is-loading="isSubmitting"
          :disabled="!form.content.trim() || !form.scheduledAt || isSubmitting"
          @click="createScheduledMessage"
        />
      </div>
    </div>
    <Button
      v-else
      size="xs"
      variant="ghost"
      icon="i-lucide-plus"
      :label="t('KANBAN.CONTACT_LIGHTBOX.SCHEDULED_MESSAGES.SCHEDULE')"
      @click="isAdding = true"
    />
  </div>
</template>
